import { useSearchParams } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { IN_PAGE_SEARCH_PARAM_KEY } from '~/modules/professional/config/constants';
import { detailCta } from '~/ui-rocco/pages/detail-shared';

/** The run under the search bar, and below, that a tab's grid would take. */
export const EMPTY_STATE_SPACING =
    'max-md:pt-9 max-md:pb-14 md:pt-[2.4rem] md:pb-[7.5rem]';

interface ProfessionalTabEmptyStateProps {
    /** What the tab lists, plural: "projects", "products". */
    resultsType: string;
    /** The tab narrows by filters as well as search, so the hint names both. */
    hasFilters?: boolean;
    className?: string;
}

/**
 * A listing tab's empty result: what came back empty, how to widen it, and
 * the search cleared in one press. No gutter or spacing of its own: it sits in
 * the tab's content column on the grid's edge, and the tab gives it the run
 * under the search bar its grid would have ({@link EMPTY_STATE_SPACING}).
 */
export const ProfessionalTabEmptyState = ({
    resultsType,
    hasFilters = false,
    className,
}: ProfessionalTabEmptyStateProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const term = searchParams.get(IN_PAGE_SEARCH_PARAM_KEY)?.trim() ?? '';

    const clearSearch = () =>
        setSearchParams(
            (current) => {
                const params = new URLSearchParams(current);
                params.delete(IN_PAGE_SEARCH_PARAM_KEY);
                params.delete('page');
                return params;
            },
            { replace: true, preventScrollReset: true },
        );

    return (
        <div
            role="status"
            className={cn('flex flex-col items-start', className)}
        >
            <p className="m-0 font-sans text-[1.5rem] leading-[1.25] font-medium tracking-[-0.025em] text-[#323232] md:text-[2.0625rem]">
                {term
                    ? `No ${resultsType} match “${term}”`
                    : `No ${resultsType} found`}
            </p>
            <p className="m-0 mt-2 font-sans text-base leading-normal text-[#767676] md:mt-3 md:text-[1.25rem]">
                {hasFilters
                    ? 'Try a different search term, or change the filters.'
                    : 'Try a different search term.'}
            </p>
            {term ? (
                <button
                    type="button"
                    onClick={clearSearch}
                    className={detailCta(
                        'white-lifted',
                        'max-md:mt-6 md:mt-8',
                        'm',
                    )}
                >
                    Clear search
                </button>
            ) : null}
        </div>
    );
};
